import { useEffect, useState } from "react";

import { Box, IconButton, InputAdornment, TextField } from "@mui/material";

import { AddCircleIcon, CloseIcon, EditNoteIcon, TickCircleIcon } from "../Icons/Icons";

export const NoteActionButtons = ({
  showNote,
  hasNote,
  onToggleNote,
  onSaveNote,
  isLoading = false,
  isSaving = false,
  size = "small",
}) => {
  if (!showNote) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          onClick={onToggleNote}
          size={size}
          disabled={isLoading || isSaving}
          sx={{
            "&:disabled": {
              color: "text.disabled",
              opacity: 0.6,
            },
          }}
        >
          {hasNote ? <EditNoteIcon /> : <AddCircleIcon />}
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <IconButton
        onClick={onSaveNote}
        size={size}
        disabled={isLoading || isSaving}
        sx={{
          "&:disabled": {
            color: "text.disabled",
            opacity: 0.6,
          },
        }}
      >
        <TickCircleIcon />
      </IconButton>
    </Box>
  );
};

export const NoteInput = ({
  note = "",
  placeholder = "Notes",
  maxLength = 100,
  onKeyDown,
  onClear,
  onChange,
  inputRef,
  isLoading = false,
  isSaving = false,
  maxWidth,
  name,
  ...textFieldProps
}) => {
  const [value, setValue] = useState(note || "");

  useEffect(() => {
    setValue(note || "");
  }, [note]);

  const handleClear = () => {
    setValue("");
    if (onClear) {
      onClear();
    }
    if (onChange) {
      onChange({ target: { value: "" } });
    }
  };

  const handleChange = (event) => {
    setValue(event.target.value);
    if (onChange) {
      onChange(event);
    }
  };

  return (
    <TextField
      label=""
      placeholder={placeholder}
      variant="outlined"
      size="small"
      multiline
      minRows={1}
      maxRows={4}
      sx={{
        maxWidth: maxWidth || "100%",
        width: "100%",
        position: "relative",
        "& .MuiInputBase-input": {
          fontSize: "14px",
        },
      }}
      inputProps={{
        maxLength,
        onKeyDown,
      }}
      name={name}
      value={value}
      onChange={handleChange}
      InputLabelProps={{ shrink: true }}
      disabled={isLoading || isSaving}
      InputProps={{
        endAdornment: value && (
          <InputAdornment position="end">
            <IconButton
              onClick={handleClear}
              onMouseDown={(event) => event.preventDefault()}
              edge="end"
              size="small"
              sx={{
                padding: "2px",
                position: "absolute",
                top: "12px",
                right: "8px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </InputAdornment>
        ),
      }}
      inputRef={inputRef}
      fullWidth
      {...textFieldProps}
    />
  );
};

export const NoteDisplay = ({ note, onEdit, maxWidth, showLabel = true }) => {
  if (!note) return null;

  return (
    <Box
      sx={{
        fontSize: "14px",
        cursor: "pointer",
        width: "auto",
        maxWidth: maxWidth || "auto",
        overflow: "hidden",
        position: "relative",

        marginLeft: "0px",
        "&:hover": {
          textDecoration: "underline",
        },
        "& .note-label": {
          color: "text.secondary",
          marginRight: "8px",
        },

        display: "flex",
      }}
      onClick={onEdit}
      title={note}
    >
      {showLabel && <span className="note-label">Note:</span>}
      <span className="note-content">{note}</span>
    </Box>
  );
};
